import { useId } from 'react';
import { useFormContext, type FieldError as FieldErrorType } from 'react-hook-form';
import formStyles from '~/styles/form.module.css';
import cnh from '~/helpers/cnh';
import useTranslation from '~/hooks/useTranslation';
import getWithDot from '~/helpers/getWithDot';
import FormLabel from './FormLabel';
import FieldError from './FieldError';

type Option = {
  label: string;
  value: string | number;
};

type Props = {
  label: string;
  registerField: string;
  options?: Option[];
  optionGroups?: {
    label: string;
    options: Option[];
  }[];
  required?: boolean;
  visuallyHiddenLabel?: boolean;
  allowEmpty?: boolean;
};

export default function SelectField({
  label,
  registerField,
  options,
  optionGroups,
  required = false,
  visuallyHiddenLabel = false,
  allowEmpty = false,
}: Props) {
  const fieldId = useId();
  const translate = useTranslation();
  const { register, formState: { errors } } = useFormContext();

  const errorForField = getWithDot<FieldErrorType>(registerField, errors);

  if (!options && !optionGroups) {
    return null;
  }

  return (
    <div>
      <FormLabel
        htmlFor={fieldId}
        visuallyHidden={visuallyHiddenLabel}
        required={required}
      >
        {label}
      </FormLabel>

      <select
        id={fieldId}
        className={cnh(formStyles.formControl, formStyles.formSelect)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...register(registerField, { required })}
        required={required}
        aria-invalid={errorForField ? 'true' : 'false'}
      >
        {allowEmpty && (
          <option value="">{translate('form.select_empty')}</option>
        )}

        {options && (
          <>
            {options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </>
        )}

        {optionGroups && (
          <>
            {optionGroups.map((optionGroup) => (
              <optgroup
                key={optionGroup.label}
                label={optionGroup.label}
              >
                {optionGroup.options.map((option, optionIndex) => (
                  <option
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${option.value}_${optionIndex}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </>
        )}
      </select>

      {errorForField && (
        <FieldError error={errorForField} />
      )}
    </div>
  );
}
